import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';
import type { StaffPermission } from './staffPermissions';

/**
 * Who the signed-in member of staff is, **according to the API** — not
 * according to the token.
 *
 * This is the capability context M1-32 exists to provide, and the reason the
 * story's acceptance criteria say the console is driven by `/admin/me` and
 * "never by a hardcoded persona check in the client". The persona → permission
 * matrix lives in `StaffPersonaPermissions` on the server (M0-33); reproducing
 * it here would create two copies that drift, and the copy in the browser
 * would be the one nobody notices is wrong.
 *
 * `permissions` is narrowed from the wire type's plain `string[]`: the API has
 * no enum for OpenAPI to generate a literal union from, so `staffPermissions`
 * stays the one place the names are declared and this asserts that what
 * `/admin/me` returns is drawn from that set — true by construction, since the
 * server builds it from `StaffPermissions.All`.
 */
export type CurrentStaff = Omit<components['schemas']['StaffMeResponse'], 'permissions'> & {
  permissions: StaffPermission[];
};

export const currentStaffQueryKey = ['admin', 'me'] as const;

/**
 * Cached for five minutes rather than refetched per screen: it changes only
 * when an Application Administrator changes an enrolment, and every guard in
 * the console reads it. The API caches its own staff lookup for the same five
 * minutes (M0-33), so matching the two means the UI never appears to update
 * ahead of what the API will actually allow.
 */
export function useCurrentStaff() {
  return useQuery({
    queryKey: currentStaffQueryKey,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<CurrentStaff> => {
      const { data, error } = await apiClient.GET('/api/v1/admin/me');
      if (error || !data) {
        // Deliberately not a friendly fallback object. A guard that cannot
        // establish what this person may do must fail closed, and it can only
        // do that if this rejects.
        throw new Error('Could not load the signed-in staff identity');
      }
      return data as CurrentStaff;
    },
  });
}
